// src/components/deal-detail/AcceptanceEmailRow.jsx
import { useState } from "react";
import { Check, AlertTriangle, Eye, Send, Pencil } from "lucide-react";
import moment from "moment";
import AcceptanceEmailMissingFields from "./AcceptanceEmailMissingFields";

export default function AcceptanceEmailRow({
  templateId,
  meta,
  recipientName,
  recipientEmail,
  sentRecord,
  missingFields,
  currentExtras,
  onSaveExtras,
  savingExtras,
  onOpenSend,
}) {
  const [expanded, setExpanded] = useState(false);
  const hasMissing = missingFields && missingFields.length > 0;
  const noRecipient = !recipientEmail;
  const sent = !!sentRecord;

  return (
    <div className="border border-slate-200 rounded-md p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          {sent ? (
            <Check size={18} className="text-emerald-600 mt-0.5 shrink-0" />
          ) : noRecipient ? (
            <AlertTriangle size={18} className="text-rose-600 mt-0.5 shrink-0" />
          ) : hasMissing ? (
            <AlertTriangle size={18} className="text-amber-500 mt-0.5 shrink-0" />
          ) : (
            <div className="w-[18px] h-[18px] rounded-full border-2 border-slate-300 mt-0.5 shrink-0" />
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">
              {meta.label}
            </div>
            {sent ? (
              <div className="text-xs text-slate-500 mt-0.5">
                Sent {moment(sentRecord.sentAt?.toDate ? sentRecord.sentAt.toDate() : sentRecord.sentAt).format("MMM D, YYYY h:mm A")}
                {sentRecord.sentByName ? ` by ${sentRecord.sentByName}` : ""}
                {" · "}
                <span className="text-slate-700">{sentRecord.recipientEmail}</span>
              </div>
            ) : noRecipient ? (
              <div className="text-xs text-rose-600 mt-0.5">No recipient — add an email on the contact.</div>
            ) : (
              <div className="text-xs text-slate-500 mt-0.5">
                To {recipientName ? `${recipientName} · ` : ""}{recipientEmail}
              </div>
            )}
            {hasMissing && !sent && (
              <button
                onClick={() => setExpanded((e) => !e)}
                className="text-xs text-amber-700 hover:text-amber-900 mt-1 underline"
              >
                {expanded ? "Hide" : "Add Missing Info"} ({missingFields.length})
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {!sent && (
            <>
              <button
                onClick={() => onOpenSend("preview")}
                disabled={noRecipient}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                title="Preview"
              >
                <Eye size={12} /> Preview
              </button>
              <button
                onClick={() => onOpenSend("edit")}
                disabled={noRecipient || hasMissing}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                title="Edit before sending"
              >
                <Pencil size={12} /> Edit
              </button>
              <button
                onClick={() => onOpenSend("send")}
                disabled={noRecipient || hasMissing}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-md bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
              >
                <Send size={12} /> Send
              </button>
            </>
          )}
          {sent && (
            <button
              onClick={() => onOpenSend("resend")}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
              title="Re-send"
            >
              <Send size={12} /> Re-send
            </button>
          )}
        </div>
      </div>

      {expanded && hasMissing && !sent && (
        <AcceptanceEmailMissingFields
          templateId={templateId}
          missingFields={missingFields}
          current={currentExtras}
          saving={savingExtras}
          onSave={async (patch) => {
            await onSaveExtras(patch);
            setExpanded(false);
          }}
          onCancel={() => setExpanded(false)}
        />
      )}
    </div>
  );
}
